import React, { useState } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Input } from '../components/UI';
import { generateReferralPDF } from '../helpers/pdfGenerator';

export default function ReferralScreen({ user, child, navigate, showToast }) {
    const [diagnosis, setDiagnosis] = useState('');
    const [intervention, setIntervention] = useState('');
    const [destination, setDestination] = useState('');
    const [urgency, setUrgency] = useState('Rutin');
    const [isSaving, setIsSaving] = useState(false);

    const handleSaveAndPrint = async () => {
        setIsSaving(true);
        try {
            const referralData = {
                diagnosis,
                intervention,
                destination,
                urgency,
                createdAt: Timestamp.now(),
                nakesId: user.uid,
                nakesName: user.username || user.email,
                childId: child.id
            };

            const ownerUid = child.ownerUid || user.uid;
            
            // Save to medical history / interventions
            await addDoc(collection(db, `users/${ownerUid}/children/${child.id}/referrals`), referralData);
            
            showToast('Rujukan berhasil disimpan.', 'success');
            
            // Generate PDF
            generateReferralPDF(child, referralData, user);
            
            navigate('viewChild', child);
        } catch (error) {
            console.error("Gagal membuat rujukan:", error);
            showToast('Gagal menyimpan rujukan.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="bg-[#F8FAFC] min-h-screen pb-20">
            <div className="bg-rose-600 text-white pt-10 pb-16 px-6 rounded-b-[2.5rem] shadow-lg relative">
                <button onClick={() => navigate('viewChild', child)} className="absolute top-6 left-4 bg-white/20 p-2 rounded-full backdrop-blur-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                </button>
                <h1 className="text-2xl font-extrabold mb-2 mt-4 text-center">Buat Surat Rujukan</h1>
                <p className="text-rose-100 text-sm text-center">Tindak lanjut klinis untuk {child.name}</p>
            </div>

            <div className="max-w-md mx-auto px-4 -mt-10 relative z-10 space-y-4">
                <div className="bg-white rounded-[2rem] p-6 shadow-xl border border-gray-100">
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center space-x-2">
                        <span>📝</span>
                        <span>Formulir Rujukan Medis</span>
                    </h3>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">Tingkat Urgensi</label>
                            <select 
                                value={urgency} 
                                onChange={e => setUrgency(e.target.value)} 
                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm font-bold rounded-xl focus:ring-rose-500 focus:border-rose-500 block p-3 shadow-sm outline-none"
                            >
                                <option value="Rutin">Rutin (Pemantauan Berkala)</option>
                                <option value="Segera">Segera (Intervensi Dini)</option>
                                <option value="Cito">Cito (Sangat Mendesak)</option>
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">Diagnosis / Temuan Klinis</label>
                            <textarea 
                                value={diagnosis} 
                                onChange={e => setDiagnosis(e.target.value)} 
                                rows="3"
                                placeholder="Contoh: Suspek Keterlambatan Bicara (Speech Delay)"
                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-rose-500 focus:border-rose-500 block p-3 shadow-sm outline-none"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">Tindakan / Intervensi Awal</label>
                            <textarea 
                                value={intervention} 
                                onChange={e => setIntervention(e.target.value)} 
                                rows="2"
                                placeholder="Contoh: Edukasi orang tua untuk stimulasi bahasa"
                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl focus:ring-rose-500 focus:border-rose-500 block p-3 shadow-sm outline-none"
                            />
                        </div>
                        
                        <Input 
                            id="destination" 
                            label="Tujuan Rujukan (Opsional)" 
                            value={destination} 
                            onChange={e => setDestination(e.target.value)} 
                            placeholder="Contoh: RSUD Kota, Spesialis Tumbuh Kembang"
                        />
                    </div>
                </div>

                <Button 
                    onClick={handleSaveAndPrint} 
                    disabled={isSaving || !diagnosis} 
                    className="w-full shadow-xl bg-gray-900 hover:bg-gray-800 py-4 text-base rounded-[1.5rem] flex items-center justify-center space-x-2"
                >
                    <span>{isSaving ? 'Menyimpan...' : 'Simpan & Cetak PDF Rujukan'}</span>
                    {!isSaving && <span>🖨️</span>}
                </Button>
            </div>
        </div>
    );
}
